import type { ModelInfo } from '@/types'

// ============================================================================
// OVERTIME STATE
// ============================================================================

export interface ProgressState {
  percentage: number | null
  isOvertime: boolean
  overtimeMessage: string | null
}

/**
 * Compute user-facing overtime state based on how long progress has been stuck at max.
 *
 * Thresholds:
 * - < 15s   → not overtime
 * - 15s–2m  → "Almost there..."
 * - 2m–5m   → "Taking longer than usual"
 * - > 5m    → "Still working — you can close this tab"
 */
export function computeOvertimeState(msAtMax: number): { isOvertime: boolean; overtimeMessage: string | null } {
  if (msAtMax < 15_000) return { isOvertime: false, overtimeMessage: null }
  if (msAtMax < 2 * 60_000) return { isOvertime: true, overtimeMessage: 'Almost there...' }
  if (msAtMax < 5 * 60_000) return { isOvertime: true, overtimeMessage: 'Taking longer than usual' }
  return { isOvertime: true, overtimeMessage: 'Still working — you can close this tab' }
}

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

// Fallback timing values when model doesn't provide estimates
const DEFAULT_GENERATION_SECONDS = 120
const DEFAULT_QUEUE_SECONDS = 30

/**
 * Buffer multiplier for timing estimates.
 * Adds padding so progress doesn't get stuck at 95% waiting for slow completions.
 * Ideal world this is 1.0, but real-world variance needs ~20% padding.
 */
const TIMING_BUFFER_MULTIPLIER = 1.2

/**
 * Maximum displayed progress percentage (0.0 - 1.0).
 * Capped at 95% to avoid the frustrating UX of progress stuck at 100% while still waiting.
 * When elapsed time equals estimate, progress shows 95%. Jumps to 100% on completion.
 */
const MAX_DISPLAYED_PROGRESS = 0.95

// ============================================================================
// TIMING UTILITIES
// ============================================================================

/**
 * How long a vessel's `generatingStartedAt` lock is honored before being
 * treated as stale (disconnected client, crashed generation). Scales with the
 * model estimate so long renders stay locked; mirrors the backend
 * GENERATION_IN_PROGRESS guard window in startGeneration.
 */
export function generationLockWindowMs(estimatedSeconds?: number | null): number {
  return Math.max(10 * 60 * 1000, 2_000 * (estimatedSeconds ?? 0))
}

/**
 * Extract estimated generation time from model info.
 *
 * Combines:
 * - Duration-specific generation time from model.inputs.duration.options (video models)
 * - OR model-level estimatedGenerationTimeSeconds (image/non-duration models)
 * - Model-level queue time estimate
 * - Buffer multiplier for real-world variance (applied only to generation time)
 *
 * @param model - Model info from backend /models endpoint
 * @param durationSeconds - Selected duration in seconds
 * @returns Total estimated time in seconds (queue + generation * safety margin)
 */
export function getTimingEstimate(model: ModelInfo, durationSeconds: number): number {
  // Find the duration option matching the selected duration
  const durationOption = model.inputs.duration?.options.find(opt => opt.value === durationSeconds)

  // Use model-provided estimates, falling back to conservative defaults
  // Priority: per-duration estimate > model-level estimate > default
  const baseGenerationTime =
    durationOption?.estimatedTimeSeconds ?? model.estimatedGenerationTimeSeconds ?? DEFAULT_GENERATION_SECONDS
  const perSourceSecond = model.estimatedGenerationSecondsPerSourceSecond
  const generationTime =
    perSourceSecond && Number.isFinite(durationSeconds) && durationSeconds > 0
      ? Math.max(baseGenerationTime, perSourceSecond * durationSeconds)
      : baseGenerationTime
  const queueTime = model.estimatedQueueTimeSeconds ?? DEFAULT_QUEUE_SECONDS

  // Apply safety margin only to generation time, not queue time
  // Queue time is more predictable and doesn't need the same variance buffer
  return queueTime + generationTime * TIMING_BUFFER_MULTIPLIER
}

/**
 * Calculate progress percentage based on elapsed time.
 *
 * Linear progression that caps at MAX_DISPLAYED_PROGRESS (95%) to avoid
 * the frustrating UX of progress stuck at 100% while still waiting.
 *
 * Progress reaches 95% exactly when elapsed time equals the estimate.
 * If generation takes longer, progress stays at 95% until completion.
 *
 * @param startedAt - Unix timestamp (ms) when generation started
 * @param estimatedTotalSeconds - Total estimated time from getTimingEstimate()
 * @param now - Current timestamp (ms), defaults to Date.now()
 * @returns Progress value from 0.0 to MAX_DISPLAYED_PROGRESS (0.95)
 */
export function calculateProgress(startedAt: number, estimatedTotalSeconds: number, now: number = Date.now()): number {
  const elapsedSeconds = (now - startedAt) / 1000
  const ratio = elapsedSeconds / estimatedTotalSeconds

  // Linear 0→1 progress, capped at 1.0, then scaled to max display (95%)
  // When elapsed equals estimate, shows 95%. Stays at 95% if takes longer.
  return Math.min(ratio, 1.0) * MAX_DISPLAYED_PROGRESS
}
